import { useField } from 'formik';
import React from 'react';

import {
  DatePicker as MUIDatePicker,
  DatePickerProps as MUIDatePickerProps,
  DateField as MUIDateField,
} from '@mui/x-date-pickers';
import { CalendarDateService, DateObject } from '@uplift/services';
import { TextFieldProps } from '@mui/material';
import { BaseTextField } from '../BaseTextField';


const DatePickerOpenedContext = React.createContext(false);

export const useDatePickerOpenedContext = () => {
  return React.useContext(DatePickerOpenedContext);
};

export type _MUIDatePickerProps = MUIDatePickerProps;

export type DatePickerProps = Omit<_MUIDatePickerProps, 'onChange' | 'value' | 'renderInput' | 'inputFormat'> & {
  name: string;
  color?: 'primary' | 'secondary';
  label?: React.ReactNode;
  fullWidth?: boolean;
  valueFormat?: string;
  keyboardOnly?: boolean;
  hideError?: boolean;
  helperText?: string;
  format?: string;
  placeholder?: string;
  showKeyboardPickerButton?: boolean;
  TextFieldComponent?: React.ComponentType<TextFieldProps>;
  style?: TextFieldProps['style'];
  transformValue?: (value: DateObject) => DateObject;
};


export const DatePicker: React.FC<DatePickerProps> = ({
  name,
  keyboardOnly,
  helperText,
  format,
  TextFieldComponent = BaseTextField,
  placeholder,
  style,
  showKeyboardPickerButton,
  transformValue,
  ...props
}) => {
  const [ open, setOpen ] = React.useState(false);
  const [ field, meta, helpers ] = useField(name);

  const onChange = (date: _MUIDatePickerProps['value']) => {
    if (CalendarDateService.isDateObject(date) && props.valueFormat) {
      helpers.setValue(date.format(props.valueFormat));
    } else {
      if (date && transformValue) {
        helpers.setValue(transformValue(date));
      } else {
        helpers.setValue(date);
      }
    }
  };

  // Allows to run validation on popup close without touching the field
  const onClose = () => setTimeout(() => helpers.setTouched(true, true));

  const valueDateObj = React.useMemo(() => {
    if (!field.value) {
      return null;
    }

    if (CalendarDateService.isDateObject(field.value)) {
      return field.value;
    }

    if (typeof field.value === 'string' && props.valueFormat) {
      return CalendarDateService.date(field.value, props.valueFormat);
    }

    return field.value;
  }, [ field.value, props.valueFormat ]);

  const textFieldProps = {
    placeholder: placeholder,
    style: style,
    error: meta.touched && Boolean(meta.error),
    helperText: meta.error || helperText,
    onBlur: () => helpers.setTouched(true),
  };

  if (keyboardOnly && !showKeyboardPickerButton) {
    return (
      <MUIDateField
        format={format}
        value={valueDateObj}
        onChange={onChange}
        onClose={onClose}
        data-testid="keyboard-date-picker"
        slots={{ textField: TextFieldComponent }}
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          textField: textFieldProps
        }}
        {...props}
      />
    );
  }

  return (
    <DatePickerOpenedContext.Provider value={open}>
      <MUIDatePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        enableAccessibleFieldDOMStructure={false}
        closeOnSelect
        slots={{
          textField: TextFieldComponent,
          toolbar: () => null,
          actionBar: () => null,
        }}
        slotProps={{
          textField: textFieldProps
        }}
        format={format}
        value={valueDateObj}
        onChange={onChange}
        {...props}
      />
    </DatePickerOpenedContext.Provider>
  );
};
